/**
 * `RuntimeState.narrative` — which narrative capital has already fired
 * (architecture.md §3.2).
 *
 * @remarks
 * Field depth: fired one-shot voice lines, read logs, fired triggers (R11 —
 * the once-per-session edge guard), and the selected endings (D151: a
 * history, terminal last). Reaction counts
 * and variant/cooldown bookkeeping remain future additions.
 *
 * Growing this shape is a SAVE-SCHEMA event: `firedTriggerIds` is what took
 * the engine schema from '1' to '2' and shipped the first real migration
 * (engine-runtime/saves). Any future field lands the same way — `spentTopicIds`
 * (D231) took it from '10' to '11', `askedPromptIds` (D235) from '13' to '14'.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { endingIdSchema, logIdSchema, promptIdSchema, topicIdSchema, triggerIdSchema } from '../identifiers.js';
import { nonNegativeIntegerSchema } from '../primitives.js';

/** Fired triggers, occurrence tallies, read logs, voice-line usage, and the selected ending. */
export const narrativeRuntimeStateSchema = z
	.strictObject({
		/**
		 * D235: the prompts an actor has already ASKED — the player's ledger,
		 * kept across a checkpoint restore like `spentTopicIds` (Mike's ruling
		 * 4: what is said stays said; a question un-asked by a rewind is an
		 * asymmetry the player can catch). A prompt that is not `repeatable`
		 * is asked at most once; a repeatable one is recorded here too, once,
		 * so "has she ever asked this" is answerable. In asking order.
		 */
		askedPromptIds: z.array(promptIdSchema).readonly(),
		/** R11: triggers that have fired — a trigger fires once per session, ever. */
		firedTriggerIds: z.array(triggerIdSchema).readonly(),
		/**
		 * R12: engine-maintained occurrence counts (`parse_failed`,
		 * `command_rejected:exit_locked`, …) — read by
		 * `occurrence_tally_at_least`; maintained whether or not anything reads
		 * them, so escalation content can arrive without a schema change.
		 */
		occurrenceTallies: z.record(z.string(), nonNegativeIntegerSchema).readonly(),
		/** R12: logs read at least once — first inspect of a carrier marks its log. */
		readLogIds: z.array(logIdSchema).readonly(),
		/**
		 * D151: the endings selected this session, in the order they were
		 * selected — a HISTORY, because an `open` ending records that a thing
		 * happened and leaves the door open behind it (D71's word, kept honest),
		 * so a session can be masked AND then die. At most one is terminal, and
		 * it is always last: a terminal ending closes the slot.
		 */
		selectedEndingIds: z.array(endingIdSchema).readonly(),
		/**
		 * D231: the `once` topics already spoken — the player's ledger, kept
		 * across a checkpoint restore like the voice ledger (Mike's ruling 4:
		 * what is said stays said). In speaking order; a topic appears once.
		 */
		spentTopicIds: z.array(topicIdSchema).readonly(),
		/**
		 * R12: the executed turn each voice line last SPOKE on — the cooldown
		 * clock. Absent key = never spoken.
		 */
		voiceLineLastSpokenTurn: z.record(z.string(), nonNegativeIntegerSchema).readonly(),
		/**
		 * R12: per-line speak counts — the D30 pool cursor AND the one-shot
		 * ledger in one truth (spoken = count > 0). Replaced R5's reserved
		 * `firedVoiceLineIds` array: two records of the same fact is the drift
		 * §2.1 refuses.
		 */
		voiceLineUsage: z.record(z.string(), nonNegativeIntegerSchema).readonly(),
	})
	.readonly();

/** The `narrative` sub-state — see {@link narrativeRuntimeStateSchema}. */
export type NarrativeRuntimeState = z.infer<typeof narrativeRuntimeStateSchema>;
